import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const VIDEO_SRC =
  "https://customer-assets.emergentagent.com/job_neon-narrative-hub/artifacts/12xws1f3_grok_video_2026-05-25-18-32-56.mp4";

const FutureOS = () => {
  const videoRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let timer;
    const onPlaying = () => {
      timer = setTimeout(() => setVisible(true), 400);
    };
    v.addEventListener("playing", onPlaying, { once: true });
    const fallback = setTimeout(() => setVisible(true), 1200);
    const p = v.play?.();
    if (p && typeof p.catch === "function") {
      p.catch(() => setVisible(true));
    }
    return () => {
      v.removeEventListener("playing", onPlaying);
      clearTimeout(timer);
      clearTimeout(fallback);
    };
  }, []);

  const ease = [0.22, 1, 0.36, 1];

  return (
    <section
      id="future"
      className="relative w-full overflow-hidden snap-section snap-reveal"
      data-testid="section-future"
    >
      <div className="relative w-full h-[80vh] min-h-[600px] md:h-[95vh] md:min-h-[720px]">
        {/* Background video */}
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          src={VIDEO_SRC}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="future-video"
        />

        {/* Dark readability overlays */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,5,10,0.7) 0%, rgba(5,5,10,0.35) 25%, rgba(5,5,10,0.4) 65%, rgba(5,5,10,0.85) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 30%, rgba(5,5,10,0.55) 100%)",
          }}
        />

        {/* Edge fades for seamless section transitions */}
        <div
          className="absolute top-0 left-0 right-0 h-32 pointer-events-none"
          style={{ background: "linear-gradient(180deg, #05050A 0%, transparent 100%)" }}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
          style={{ background: "linear-gradient(0deg, #05050A 0%, transparent 100%)" }}
        />

        {/* Scanlines */}
        <div
          className="absolute inset-0 pointer-events-none opacity-25"
          style={{
            backgroundImage:
              "repeating-linear-gradient(to bottom, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 4px)",
            mixBlendMode: "overlay",
          }}
        />

        {/* Top HUD micro labels */}
        <div className="absolute top-24 md:top-28 left-5 md:left-10 right-5 md:right-10 flex items-center justify-between z-10 pointer-events-none">
          <span className="micro-label text-white/55">// CH.11 — FUTURE.OS</span>
          <span className="micro-label text-white/55 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-dot" />
            PORTAL · OPEN
          </span>
        </div>

        {/* Centered overlay copy */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
            transition={{ duration: 1.0, ease }}
            className="micro-label text-cyan-300 mb-4 md:mb-6 interactive-text interactive-text--cyan"
            style={{ textShadow: "0 0 16px rgba(0,240,255,0.45)" }}
            data-testid="future-eyebrow"
          >
            // STEP THROUGH THE PORTAL
          </motion.div>

          {/* Title — Future.OS */}
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
            transition={{ duration: 1.2, ease, delay: 0.2 }}
            className="font-display font-black uppercase tracking-tighter leading-[0.92] text-white interactive-text interactive-text--white"
            style={{
              fontSize: "clamp(2.5rem, 9vw, 7.5rem)",
              textShadow:
                "0 0 28px rgba(255,170,80,0.4), 0 0 60px rgba(0,240,255,0.25), 0 2px 22px rgba(0,0,0,0.75)",
            }}
            data-testid="future-title"
          >
            Future
            <span className="text-cyan-300 interactive-text interactive-text--cyan" style={{ textShadow: "0 0 22px rgba(0,240,255,0.7)" }}>.</span>
            OS
          </motion.h2>

          {/* Accent bar */}
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={visible ? { opacity: 1, width: "140px" } : { opacity: 0, width: 0 }}
            transition={{ duration: 1.1, ease, delay: 0.55 }}
            className="h-[1.5px] my-7 md:my-9"
            style={{
              background:
                "linear-gradient(90deg, transparent, #FFB845 30%, #00F0FF 75%, transparent)",
              boxShadow: "0 0 12px rgba(255,184,69,0.45)",
            }}
            data-testid="future-bar"
          />

          {/* Sub-headline */}
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
            transition={{ duration: 1.05, ease, delay: 0.75 }}
            className="font-display font-bold uppercase tracking-tight leading-[1.1] text-white max-w-3xl interactive-text interactive-text--white"
            style={{
              fontSize: "clamp(1.05rem, 2.6vw, 2rem)",
              textShadow: "0 2px 20px rgba(0,0,0,0.75)",
            }}
            data-testid="future-subheadline"
          >
            Step into the Future of{" "}
            <span
              className="inline-block interactive-text interactive-text--gold"
              style={{
                color: "#FFB845",
                textShadow: "0 0 22px rgba(255,184,69,0.55)",
              }}
            >
              Media
            </span>{" "}
            &amp;{" "}
            <span
              className="inline-block text-cyan-300 interactive-text interactive-text--cyan"
              style={{ textShadow: "0 0 22px rgba(0,240,255,0.55)" }}
            >
              Content Tech
            </span>
            .
          </motion.p>

          {/* Caption */}
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
            transition={{ duration: 1.0, ease, delay: 1.05 }}
            className="mt-5 md:mt-7 max-w-xl text-white text-sm md:text-base leading-relaxed space-y-1"
            data-testid="future-caption"
          >
            <span className="block interactive-text interactive-text--white">
              The next chapter of Bharat&apos;s media ecosystem begins here.
            </span>
            <span className="block">
              <span className="text-cyan-300 interactive-text interactive-text--cyan" style={{ textShadow: "0 0 16px rgba(0,240,255,0.55)" }}>Raw.</span>{" "}
              <span className="interactive-text interactive-text--gold" style={{ color: "#FFB845", textShadow: "0 0 16px rgba(255,184,69,0.55)" }}>Unbiased.</span>{" "}
              <span className="text-fuchsia-300 interactive-text interactive-text--magenta" style={{ textShadow: "0 0 16px rgba(255,0,255,0.55)" }}>Independent.</span>
            </span>
            <span className="block interactive-text interactive-text--white">Get instant access to millions.</span>
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default FutureOS;
